// StreamForm.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// CSS
import './StreamForm.css';

// Components
import Button from 'components/button/Button';
import { functions, httpsCallable } from 'firebaseConfig';

/**
 * StreamForm
 *
 * @returns {ReactNode} The rendered component.
 */
const StreamForm = ({ tempNode, handleTempNodeChange }) => {

  // ----- CONTEXT ----- //

  /* ----- CONTEXT ----- */

  // Theme Context

  const { theme } = useTheme();

  // User Context
  const { user } = useUser();

  // ----- STATE ----- //

  /* ----- STATE ----- */

  // Loading state

  const [loading, setLoading] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Stream details state
  const [streamDetails, setObsDetails] = useState(null);

  // ----- REFS ----- //

  const loadingRef = useRef(false);
  const streamDetailsRef = useRef(null);
  const securedLiveStreamRef = useRef(false);

  // ----- STATE ----- //

  // Copied field state

  const [copiedField, setCopiedField] = useState(null);

  /* ----- MEMOIZED VALUES ----- */

  // Node key
  const nodeKey = tempNode?.key || null;

  // ----- MEMOS ----- //

  // Is owner

  const isOwner = useMemo(() => {
    if (!user || !nodeKey) return false;
    const ownerKey = tempNode?.userKey || tempNode?.ownerKey || null;
    const currentKey = user.userKey || user.key || null;
    return Boolean(ownerKey && currentKey && ownerKey === currentKey);
  }, [user, nodeKey, tempNode]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    streamDetailsRef.current = streamDetails;
  }, [streamDetails]);

  const securedLiveStream = tempNode?.secured?.liveStream;
  useEffect(() => {
    securedLiveStreamRef.current = Boolean(securedLiveStream);
  }, [securedLiveStream]);

  /**
   * Initializes Mux live stream for the node if conditions are met.
   */
  useEffect(() => {
    let cancelled = false;

    const maybeInitMuxStream = async () => {
      if (!nodeKey || !isOwner || loadingRef.current) return;

      // If we already have secured liveStream info or streamDetails, do nothing.
      if (securedLiveStreamRef.current || streamDetailsRef.current) return;

      try {
        setLoading(true);
        setError(null);
        const fn = httpsCallable(functions, 'createLiveStreamForNode');
        const res = await fn({ nodeId: nodeKey });

        if (cancelled) return;

        const { ingestUrl, streamKey, muxLiveStreamId, reused } = res?.data || {};
        setObsDetails({ ingestUrl, streamKey, muxLiveStreamId, reused: !!reused });
      } catch (err) {
        if (cancelled) return;
        console.error('StreamForm: failed to initialize live stream', {
          error: err,
          code: err?.code,
          message: err?.message,
          details: err?.details,
        });
        setError('Unable to create live stream. You can try again later.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    maybeInitMuxStream();

    return () => {
      cancelled = true;
    };
  }, [nodeKey, isOwner]);

  // ----- HANDLERS ----- //

  /**
   * Handles copy to clipboard for a given value and field identifier.
   * 
   * @param {string} value - The value to copy to clipboard.
   * @param {string} field - The field identifier ('ingest' or 'key').
   */

  const handleCopy = async (value, field) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => {
        setCopiedField((current) => (current === field ? null : current));
      }, 1500);
    } catch (err) {
      console.error('StreamForm: failed to copy to clipboard', err);
    }
  };

  /**
   * Handles Download OBS button click.
   */
  const handleDownloadObsClick = () => {
    window.open('https://obsproject.com/download', '_blank', 'noopener');
  };

  // ----- RENDER ----- //

  return (

    <div className="stream-form-container">

      {/* STATUS MESSAGE */}
      {loading &&

        <div
          className="stream-form-status"
        >
          Setting up your live stream…
        </div>
      }

      {/* ERROR MESSAGE */}
      {error &&

        <div
          className="stream-form-error"
          style={{
            color: theme.warning
          }}
        >
          {error}
        </div>
      }

      {streamDetails && (

        <div className="stream-form-details">

          {/* FIELD WRAPPER */}
          <div className="stream-form-field-wrapper">

            {/* LABEL*/}

            <div
              className="stream-form-field-label"
            >
              Ingest URL
            </div>

            {/* VALUE WRAPPER */}
            <div className="stream-form-value-wrapper">

              {/* VALUE */}
              <div
                className="stream-form-value"
              >
                {streamDetails.ingestUrl || '—'}
              </div>

              <Button
                label={copiedField === 'ingest' ? 'Copied' : 'Copy'}
                height="38px"
                borderRadius="9px"
                onClick={() => handleCopy(streamDetails.ingestUrl, 'ingest')}
              />
            </div>

          </div>

          {/* FIELD WRAPPER */}
          <div className="stream-form-field-wrapper">

            {/* LABEL*/}

            <div
              className="stream-form-field-label"
            >
              Stream Key
            </div>

            {/* VALUE WRAPPER */}
            <div className="stream-form-value-wrapper">

              {/* VALUE */}
              <div
                className="stream-form-value"
              >
                {streamDetails.streamKey || '—'}
              </div>

              <Button
                label={copiedField === 'key' ? 'Copied' : 'Copy'}
                height="38px"
                borderRadius="9px"
                onClick={() => handleCopy(streamDetails.streamKey, 'key')}
              />
            </div>
          </div>

          {/* OBS DOWNLOAD BUTTON */}
          <div className="stream-form-obs-button">
            <Button
              label="Download OBS Studio"
              height="38px"
              borderRadius="9px"
              onClick={handleDownloadObsClick}
            />
          </div>

        </div>
      )}

      {/* STATUS MESSAGE */}
      {!loading && !streamDetails && !error && isOwner && (

        <div
          className="stream-form-status"
        >
          Preparing livestream details…
        </div>
      )}
    </div>
  );
};

// Dev Tools
StreamForm.displayName = 'StreamForm';

export default StreamForm;
